// src/api/uploads.js
//
// ЗАГРУЗКА МАКЕТА КЛИЕНТА (этап 1.1–1.2 SHOP_PLAN.txt, сессия 9).
//
// ═══════════════════════════════════════════════════════════════════════
// 🔑 ПОЧЕМУ ЗАГРУЗКА ОТДЕЛЬНЫМ ЗАПРОСОМ, А НЕ ВМЕСТЕ С ЗАКАЗОМ
//
// Соблазн: приложить файл к `POST /orders` в том же `multipart`. Это дефект:
//   1. ЗАКАЗ — JSON, а файл требует `multipart/form-data`. Смешивать две
//      формы в одном запросе значит переписать контракт создания заказа,
//      который работает (и покрыт проверками);
//   2. ФАЙЛ ВЕСИТ ДО 20 МБ. Если клиент заполнял форму 5 минут и ошибся
//      в одном поле — при отправке «одним куском» ему пришлось бы заливать
//      20 МБ ПОВТОРНО. Загрузка заранее этого не требует: файл уже на
//      сервере, заказ ссылается на него;
//   3. ПРОГРЕСС. Отдельный запрос позволяет показать ход загрузки.
//
// ⇒ Схема: ① файл → `POST /uploads/layout` (получаем СНИМОК),
//          ② заказ → `POST /orders` со снимком в `layout_file` позиции.
//    Снимок — не сам файл, а его метаданные; повторно он не проверяется,
//    потому что файл уже прошёл белый список MIME И магические байты
//    (см. `backend/src/middleware/upload.js`).
//
// ⚠️ ГОСТЮ ЗАГРУЗКА ДОСТУПНА. Это не недосмотр: оформление заказа открыто
//    без входа, и если бы макет требовал токен, ГОСТЬ НЕ СМОГ БЫ ЕГО
//    ПРИЛОЖИТЬ — ключевая функция агентства работала бы только для
//    зарегистрированных. Разбор — в `backend/src/routes/uploadRoutes.js`.
// ═══════════════════════════════════════════════════════════════════════

import client from './client.js';

/**
 * Загрузить один файл макета.
 *
 * @param {File} file — файл из `<input type="file">`.
 * @param {(percent: number) => void} [onProgress] — прогресс 0…100.
 *        ⚠️ `onProgress` может НЕ вызваться ни разу (например, при
 *        мгновенной ошибке): код, который ждёт 100 %, обязан иметь
 *        собственное завершение по `finally`.
 * @returns {Promise<{path: string, original_name: string, mime: string,
 *                    size: number, uploaded_at: string}>} снимок макета —
 *        ровно то, что кладётся в `order_items.layout_file`.
 */
export const uploadsAPI = {
  uploadLayout: (file, onProgress) => {
    const form = new FormData();
    // ⚠️ Имя поля — `file`. Оно задано в `uploadLayout.single('file')`
    //    (`middleware/upload.js`); с другим именем multer ответит
    //    `LIMIT_UNEXPECTED_FILE`.
    form.append('file', file);

    return client.post('/uploads/layout', form, {
      // ⚠️ `Content-Type` НЕ ставим вручную. Браузер обязан сам добавить
      //    `multipart/form-data; boundary=...`. Если задать заголовок
      //    вручную, boundary не появится, и сервер не разберёт тело.
      //    Axios при передаче `FormData` сам убирает прежний Content-Type.
      onUploadProgress: (e) => {
        if (!onProgress || !e.total) return;
        onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
  },

  /**
   * Удалить ранее загруженный макет (клиент передумал до оформления).
   * @param {string} filename — имя на диске (UUID), НЕ путь целиком.
   *        ⚠️ Из снимка берём ПОСЛЕДНИЙ сегмент `path`: там `layouts/<uuid>`,
   *        а маршрут принимает только `<uuid>`. Передать путь целиком
   *        нельзя — имя проверяется на сервере (обход каталога).
   */
  deleteLayout: (filename) =>
    client.delete(`/uploads/layout/${encodeURIComponent(filename)}`),
};
